import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import {
  Home,
  FileText,
  Users,
  Building2,
  ScanLine,
  HelpCircle,
  LayoutDashboard,
  SlidersHorizontal,
  BellRing,
  Clock,
  Siren,
  ShieldCheck,
  ClipboardCheck,
  BarChart3,
  X,
} from 'lucide-react';
import { isAnalyticsPath } from '../../utils/analyticsConfig';
import { useOptionalResponsiveLayout } from '../../context/ResponsiveLayoutContext';

export interface AppSidebarMenuItem {
  title: string;
  path: string;
  icon?: string;
}

const SIDEBAR_ICONS: Record<
  string,
  React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>
> = {
  Home,
  ScanLine,
  FileText,
  Users,
  Building2,
  LayoutDashboard,
  SlidersHorizontal,
  BellRing,
  Clock,
  Siren,
  ShieldCheck,
  ClipboardCheck,
  BarChart3,
};

const ICON_STROKE_WIDTH = 2;

interface AppSidebarProps {
  menuItems?: AppSidebarMenuItem[];
}

function AppSidebar({ menuItems = [] }: Readonly<AppSidebarProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const layout = useOptionalResponsiveLayout();
  const isDrawerMode = layout?.isDrawerMode ?? false;
  const isOpen = layout ? layout.isNavOpen : true;
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (isAnalyticsPath(path)) {
      return isAnalyticsPath(location.pathname);
    }
    return location.pathname.startsWith(path);
  };

  const showTooltip = useCallback(
    (e: React.MouseEvent<HTMLElement>, text: string) => {
      if (isDrawerMode) {
        return;
      }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setTooltip({
        text,
        x: rect.right + 8,
        y: rect.top + rect.height / 2,
      });
    },
    [isDrawerMode]
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isDrawerMode) {
      layout?.closeAllNav();
    }
  };

  return (
    <aside
      className={classNames(
        'app-sidebar',
        isDrawerMode && 'app-sidebar--drawer',
        isDrawerMode && isOpen && 'app-sidebar--open'
      )}
      aria-label="Navigation"
      aria-hidden={isDrawerMode && !isOpen ? true : undefined}
    >
      <div className="app-sidebar-top">
        <div
          className="app-sidebar-logo"
          title="RadiologyIQ"
        >
          <img
            src="/assets/iq.png"
            alt="RadiologyIQ"
            className="app-sidebar-logo-icon"
            onError={e => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        {isDrawerMode ? (
          <button
            type="button"
            className="app-sidebar-close"
            onClick={layout?.closeAllNav}
            aria-label="Close navigation"
          >
            <X
              size={20}
              strokeWidth={2}
            />
          </button>
        ) : null}
      </div>
      <nav className="app-sidebar-nav">
        {menuItems.map((item, index) => {
          const active = isActive(item.path);
          const iconKey = item.icon;
          const IconComponent = iconKey ? SIDEBAR_ICONS[iconKey] : null;
          return (
            <div
              key={item.path + index}
              className="app-sidebar-item-wrap"
              onMouseEnter={e => showTooltip(e, item.title)}
              onMouseLeave={hideTooltip}
            >
              <button
                type="button"
                onClick={() => handleNavigate(item.path)}
                className={`app-sidebar-item ${active ? 'active' : ''}`}
                aria-label={item.title}
                aria-current={active ? 'page' : undefined}
              >
                {IconComponent ? (
                  <IconComponent
                    className="app-sidebar-icon"
                    strokeWidth={ICON_STROKE_WIDTH}
                    size={24}
                  />
                ) : (
                  <span
                    className="app-sidebar-icon"
                    aria-hidden
                  >
                    •
                  </span>
                )}
                {isDrawerMode ? <span className="app-sidebar-item-label">{item.title}</span> : null}
              </button>
            </div>
          );
        })}
      </nav>
      <div
        className="app-sidebar-item-wrap"
        onMouseEnter={e => showTooltip(e, 'Help')}
        onMouseLeave={hideTooltip}
      >
        <button
          type="button"
          className="app-sidebar-help"
          onClick={() => {}}
          aria-label="Help"
        >
          <HelpCircle
            className="app-sidebar-icon"
            strokeWidth={ICON_STROKE_WIDTH}
            size={24}
          />
          {isDrawerMode ? <span className="app-sidebar-item-label">Help</span> : null}
        </button>
      </div>
      {tooltip && !isDrawerMode
        ? createPortal(
            <div
              className="app-sidebar-tooltip app-sidebar-tooltip-portal"
              role="tooltip"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                transform: 'translateY(-50%)',
              }}
            >
              {tooltip.text}
            </div>,
            document.body
          )
        : null}
    </aside>
  );
}

export default AppSidebar;
